import { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import UploadFrame from "../../../assets/images/UploadFrame.png";
import PinIcon from "../../../assets/images/Pin.png";
import { CloseIcon } from "../../../assets/icons";
import { IconButton } from "@mui/material";
import {
  isFileTypeAllowed,
} from "../../../utils/fileValidation";
import {
  Overlay,
  Modal,
  CloseButton,
  Header,
  UploadLabel,
  DropZone,
  UploadIconWrapper,
  UploadText,
  UploadLink,
  Actions,
  Btn,
  NextButton,
  UploadedFile,
  UploadedFileInfo,
  UploadedFileName,
  UploadedFileClose,
} from "../../../components/UploadButton/uploadButtonModals/SelectTemplateStyled";

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #0c53bb;
`;

const CheckboxLabel = styled.label`
  font-family: "Manrope", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #1b2028;
  cursor: pointer;
  user-select: none;
`;

export default function UploadTemplateModal({
  open,
  onClose,
  onUpload,
  uploading,
  handleError,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [file, setFile] = useState(null);
  const [showUploadedFile, setShowUploadedFile] = useState(false);
  const [isLetterheadEnabled, setIsLetterheadEnabled] = useState(false);
  const inputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (!isFileTypeAllowed(droppedFile)) {
        handleError?.("Only PDF, DOCX, or XLSX files are allowed.");
        return;
      }
      setFile(droppedFile);
      setShowUploadedFile(true);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const selectedFile = e.target.files[0];
      if (!isFileTypeAllowed(selectedFile)) {
        handleError?.("Only PDF, DOCX, or XLSX files are allowed.");
        return;
      }
      setFile(selectedFile);
      setShowUploadedFile(true);
    }
  };

  const handleRemoveFile = () => {
    setShowUploadedFile(false);
    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = null;
    }
  };

  const handleClose = () => {
    setFile(null);
    setShowUploadedFile(false);
    setIsLetterheadEnabled(false);
    if (inputRef.current) {
      inputRef.current.value = null;
    }
    onClose();
  };

  const handleUpload = () => {
    if (!file) return;
    onUpload(file, isLetterheadEnabled);
    // Don't clear state here - let it remain visible during upload
    // State will be cleared when modal closes (on success or cancel)
  };

  // Clear file state when modal closes (including after successful upload)
  useEffect(() => {
    if (!open) {
      setFile(null);
      setShowUploadedFile(false);
      setIsLetterheadEnabled(false);
      if (inputRef.current) {
        inputRef.current.value = null;
      }
    }
  }, [open]);

  const stopDragEvents = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  if (!open) return null;

  return (
    <Overlay
      onDragEnter={stopDragEvents}
      onDragOver={stopDragEvents}
      onDrop={stopDragEvents}
    >
      <Modal>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
        <Header>Select Template from System</Header>
        {!showUploadedFile && (
          <DropZone
            isDragActive={dragActive}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => inputRef.current.click()}
          >
            <UploadIconWrapper>
              <img src={UploadFrame} alt="Upload" />
            </UploadIconWrapper>
            <UploadText>
              Drag a file template in pdf, doc, docx formats here or{" "}
              <UploadLink
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current.click();
                }}
              >
                click to upload
              </UploadLink>
            </UploadText>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xlsx"
              style={{ display: "none" }}
              onChange={handleFileChange}
              data-testid="upload-modal-file-input"
            />
          </DropZone>
        )}
        {showUploadedFile && file && (
          <UploadedFile>
            <UploadedFileInfo>
              <img src={PinIcon} alt="Attachment" width={24} height={24} />
              <UploadedFileName>{file.name}</UploadedFileName>
            </UploadedFileInfo>
            <UploadedFileClose
              aria-label="Remove file"
              onClick={handleRemoveFile}
            >
              <CloseIcon />
            </UploadedFileClose>
          </UploadedFile>
        )}
        <CheckboxWrapper>
          <Checkbox
            type="checkbox"
            id="letterhead-checkbox"
            checked={isLetterheadEnabled}
            onChange={(e) => setIsLetterheadEnabled(e.target.checked)}
          />
          <CheckboxLabel htmlFor="letterhead-checkbox">
            Does this template require a letterhead?
          </CheckboxLabel>
        </CheckboxWrapper>
        <Actions>
          <Btn type="button" onClick={handleClose}>
            Cancel
          </Btn>
          <NextButton
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            data-testid="upload-modal-submit"
          >
            {uploading ? "Uploading..." : "Upload"}
          </NextButton>
        </Actions>
      </Modal>
    </Overlay>
  );
}

UploadTemplateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  handleError: PropTypes.func,
};
